import { useEffect, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Speaker } from "lucide-react";
import { useMusicPlayer } from "../lib/musicPlayer";
import AlbumArt from "./AlbumArt";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function MusicPlayerBar() {
  const {
    current, isPlaying, progress, togglePlay, next, prev, seek, hasNext, hasPrev,
    outputDevices, sinkId, sinkSupported, refreshOutputDevices, selectOutput,
  } = useMusicPlayer();
  const [showOutputs, setShowOutputs] = useState(false);

  useEffect(() => {
    if (showOutputs) refreshOutputDevices();
  }, [showOutputs, refreshOutputDevices]);

  if (!current) return null;

  const pct = progress.duration ? (progress.position / progress.duration) * 100 : 0;

  const onSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    seek(ratio * (progress.duration || 0));
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur border-t border-[#222] px-4 md:px-8 py-3" data-testid="music-player-bar">
      <div className="max-w-[1500px] mx-auto flex items-center gap-4">
        <div className="w-10 h-10 shrink-0 bg-[#0F0F0F] overflow-hidden">
          <AlbumArt url={current.album_art_url} size="sm" />
        </div>
        <div className="min-w-0 w-40 md:w-56">
          <p className="text-white text-sm truncate">{current.title}</p>
          <p className="text-[#8A8A8A] text-xs truncate">{current.artist}</p>
        </div>
        <button onClick={prev} disabled={!hasPrev} className="text-[#8A8A8A] hover:text-white disabled:opacity-30" data-testid="music-prev">
          <SkipBack size={16} />
        </button>
        <button onClick={togglePlay} className="w-8 h-8 flex items-center justify-center bg-[#D9381E] hover:bg-[#ED4B32] text-white" data-testid="music-play-pause">
          {isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
        </button>
        <button onClick={next} disabled={!hasNext} className="text-[#8A8A8A] hover:text-white disabled:opacity-30" data-testid="music-next">
          <SkipForward size={16} />
        </button>
        <span className="text-[10px] text-[#8A8A8A] w-9 text-right shrink-0">{fmt(progress.position)}</span>
        <div className="flex-1 h-1 bg-white/10 cursor-pointer hidden sm:block" onClick={onSeek} data-testid="music-seek">
          <div className="h-full bg-[#D9381E]" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] text-[#8A8A8A] w-9 shrink-0">{fmt(progress.duration)}</span>

        {sinkSupported && (
          <div className="relative shrink-0">
            <button onClick={() => setShowOutputs((v) => !v)} className="text-[#8A8A8A] hover:text-white" title="Output device" data-testid="music-output-toggle">
              <Speaker size={16} />
            </button>
            {showOutputs && (
              <div className="absolute bottom-full right-0 mb-2 w-56 bg-[#0F0F0F] border border-[#222] py-1" data-testid="music-output-list">
                {outputDevices.length === 0 && (
                  <p className="px-3 py-2 text-xs text-[#8A8A8A]">No output devices found</p>
                )}
                {outputDevices.map((d) => (
                  <button key={d.deviceId} onClick={() => { selectOutput(d.deviceId); setShowOutputs(false); }}
                    className={`w-full text-left px-3 py-2 text-xs truncate ${sinkId === d.deviceId ? "text-[#D9381E]" : "text-white hover:bg-white/5"}`}
                    data-testid={`music-output-${d.deviceId}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
